import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Rive } from '@rive-app/canvas';

import { EditProps, MAX_TEXT_LABELS, TextRun } from './rive.constants';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import {
  FileDropperContextProvider,
  useFileDropperContext,
} from 'lib/form/file-dropper/file-dropper.context';
import {
  SettingsGroup,
  SettingsLabel,
  SettingSmallTextEllipsis,
  SettingSmallText,
} from 'approot/activity-builder/settings/settings.style';
import { FileDropperRive } from './file-dropper-rive';
import { ACCEPT_TYPE_RIVE } from 'lib/form/file-dropper/file-dropper.constants';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import {
  useAddRiveFileToComponent,
  useFindComponentDimensions,
} from '../component.hooks';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { ComponentActions } from '../components.style';
import { CONTENT_IMAGE } from 'global/constants';
import { useImageRequestLoader } from 'approot/shared/images/images.hooks';
import DeleteButtonIcon from 'approot/shared/styles/buttons/delete-icon-button';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import TextInputLimit from 'approot/shared/text-input-limit/text-input-limit';
import { GENERATE_IMAGE_ALT_TEXT } from 'approot/dictionary/dictionary.graphql';
import type {
  GenerateImageAltTextQuery,
  GenerateImageAltTextQueryVariables,
} from '../../../dictionary/dictionary.graphql.generated';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import RiveAnimation from './rive-animation';
import { RiveFileUploadWrapper } from './rive-modal.style';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import ImageUploadField, { ImageProps } from '../shared/image-upload-field';
import ComponentImageSource from '../shared/component-image-modal/component-image-source';
import ComponentAltTextTooltip from '../shared/component-alt-text/component-alt-text-tooltip';
import { removeTypename } from 'approot/shared/logic/remove-typename';
import { ALT_TEXT_MAX } from '../shared/component-alt-text/component-alt-text.constants';
import { DIRECTION_LEFT } from 'webclient.constants';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import customNanoId from 'lib/nanoid/nanoid';
import { spacing16 } from 'approot/shared/styles/spacing.style';
import CheckboxInput from 'lib/form/checkbox-input/checkbox-input';
import { isInOldRiveFormat } from './rive.utils';

const RIVE_KEY = 'riveKey';
const IMAGE_KEY = 'imageKey';
const ALT_TEXT_FIELD = 'altText';

type FormData = {
  [RIVE_KEY]?: string | null;
  [IMAGE_KEY]?: string | null;
  [ALT_TEXT_FIELD]?: string | null;
};

type Props = EditProps & {
  fileToUpload?: {
    key: string;
    fileName: string;
    file: File;
  };
  onClose: () => void;
};

const RiveModal = ({
  id,
  sectionId,
  properties,
  fileToUpload,
  onClose,
}: Props) => {
  const [isShowRiveDropper, setIsShowRiveDropper] = useState<boolean>(false);

  const [imageData, setImageData] = useState<ImageProps | null>(
    properties.image
  );
  const [sourceUrl, setSourceUrl] = useState<string>(
    properties.sourceUrl || ''
  );

  const [riveFileData, setRiveFileData] = useState<{
    key?: string | null;
    fileName?: string | null;
    link?: string | null;
    file?: File;
  } | null>(fileToUpload ? fileToUpload : properties.rive);

  const [autoExtractLabels, setAutoExtractLabels] = useState<boolean>(false);
  const riveInstance = useRef<Rive | null>(null);

  const { elementDimensionLabel } = useFindComponentDimensions(id);
  const { contentId, contentType } = useGetInteractiveLessonParams();

  const { loading: loadingAddRive, addRiveFileToComponent } =
    useAddRiveFileToComponent({
      onCompleted: () => onClose(),
    });

  const [generateAltText, { loading: loadingAltText }] = useLoggingLazyQuery<
    GenerateImageAltTextQuery,
    GenerateImageAltTextQueryVariables
  >(GENERATE_IMAGE_ALT_TEXT, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const altText = res.generateImageAltText.altText.substring(0, 1000);
      setValue(ALT_TEXT_FIELD, altText);
      const altTextElement = document.getElementById(ALT_TEXT_FIELD);
      altTextElement?.focus();
    },
  });

  const { clearFile } = useFileDropperContext(IMAGE_KEY);
  const { clearFile: clearRiveFile } = useFileDropperContext(RIVE_KEY);

  const formMethods = useForm<FormData>({
    defaultValues: {
      [RIVE_KEY]: riveFileData?.key,
      [IMAGE_KEY]: properties.image?.key,
      [ALT_TEXT_FIELD]: properties.image?.alt,
    },
  });
  const {
    formState: { errors },
    register,
    handleSubmit,
    setValue,
  } = formMethods;

  const { imageUrl, getImageResponsive, setImageUrl } = useImageRequestLoader({
    url: properties.image?.original,
  });

  const handleRiveFileChange = useCallback(
    ({
      key,
      fileName,
      file,
    }: {
      key: string;
      fileName: string;
      file: File;
    }) => {
      riveInstance.current = null;
      setRiveFileData({
        ...riveFileData,
        key,
        fileName,
        link: URL.createObjectURL(file),
      });
      setValue(RIVE_KEY, key, { shouldValidate: true });
      setIsShowRiveDropper(false);
    },
    [riveFileData, setValue, setRiveFileData, setIsShowRiveDropper]
  );

  const handleFileChange = useCallback(
    (key: string, fileName: string) => {
      getImageResponsive({
        variables: {
          input: {
            key: key,
            imageType: CONTENT_IMAGE,
          },
        },
      }).then(res => {
        setImageData({
          ...imageData,
          key,
          fileName,
          original: res.data?.getImageResponsive.original,
        });
      });
      setValue(IMAGE_KEY, key, { shouldValidate: true });
    },
    [imageData, setImageData, setValue, getImageResponsive]
  );

  const handleDeleteImage = useCallback(() => {
    setImageData(null);
    setValue(IMAGE_KEY, undefined);
    clearFile();
    setImageUrl(undefined);
  }, [clearFile, setImageUrl, setImageData, setValue]);

  const extractTextRunsFromRive = useCallback((): TextRun[] | undefined => {
    if (!riveInstance.current) {
      return undefined;
    }

    const labels: TextRun[] = [];

    try {
      // check if the rive file is in the old format
      const isOldRiveFormat = isInOldRiveFormat(riveInstance.current);

      if (isOldRiveFormat) {
        for (let i = 1; i <= MAX_TEXT_LABELS; i++) {
          const value = riveInstance.current.getTextRunValue(`run${i}`);
          if (value && value.trim() !== '') {
            labels.push({
              __typename: 'RiveTextRun',
              itemId: customNanoId(),
              text: value,
              sort: getReorderSort(labels, labels.length, DIRECTION_LEFT),
            });
          }
        }
      } else {
        const result = riveInstance.current.viewModelInstance;

        if (result) {
          const properties = result.properties.filter(
            p => p.type.toString() === 'string'
          );

          for (let i = 0; i < properties.length; i++) {
            const value = result.string(properties[i].name)?.value;

            if (value && value.trim() !== '') {
              labels.push({
                __typename: 'RiveTextRun',
                itemId: customNanoId(),
                text: value,
                sort: getReorderSort(labels, labels.length, DIRECTION_LEFT),
              });
            }
          }
        }
      }

      // if the file contained no text labels return an empty array
      // as the user explicitly checked "auto extract"
      // however return undefined in error cases and this won't wipe existing labels
      return labels;
    } catch {
      return undefined;
    }
  }, []);

  const onSubmit = useCallback(
    (data: FormData) => {
      const imageKey = data[IMAGE_KEY];
      if (!riveFileData || !imageKey || !imageData?.fileName) return;

      let textLabels = properties.rive?.textLabels;
      let enableTextLabels = properties.rive?.enableTextLabels;

      if (autoExtractLabels) {
        const extractedTextRuns = extractTextRunsFromRive();
        if (extractedTextRuns) {
          textLabels = extractedTextRuns;
          enableTextLabels = true;
        }
      }

      addRiveFileToComponent({
        variables: {
          input: {
            componentId: id,
            contentId,
            contentType,
            sectionId,
            riveInput: removeTypename({
              fileName: riveFileData.fileName!,
              key: riveFileData.key!,
              sourceUrl,
              buttons: properties.rive?.buttons,
              textLabels,
              enableButtons: properties.rive?.enableButtons,
              enableResetButton: properties.rive?.enableResetButton,
              enableTextLabels,
              buttonsPosition: properties.rive?.buttonsPosition,
            }),
            imageInput: {
              key: imageKey,
              imageType: CONTENT_IMAGE,
              fileName: imageData.fileName,
              alt: data[ALT_TEXT_FIELD] || null,
            },
          },
        },
      });
    },
    [
      id,
      sectionId,
      contentId,
      contentType,
      imageData,
      riveFileData,
      sourceUrl,
      properties,
      addRiveFileToComponent,
      autoExtractLabels,
      extractTextRunsFromRive,
    ]
  );

  const isUpdating = loadingAddRive;

  useEffect(() => {
    register(RIVE_KEY, {
      required: false,
    });
    register(IMAGE_KEY, {
      required: 'Oops! It looks like you haven’t set an image for print.',
    });
  }, [register]);

  return (
    <ActivityBuilderModal
      isVisible
      title="Rive Animation"
      headerTitle="Rive Animation"
      onClose={onClose}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsGroup>
            <Box $justifyContent="space-between">
              <SettingsLabel>Rive Animation</SettingsLabel>
              {riveFileData?.key && (
                <SettingSmallText marginBottom={0}>
                  {elementDimensionLabel}
                </SettingSmallText>
              )}
            </Box>
            {!isShowRiveDropper && riveFileData?.key ? (
              <RiveFileUploadWrapper>
                <RiveAnimation
                  key={riveFileData?.key}
                  link={
                    riveFileData?.file
                      ? URL.createObjectURL(riveFileData.file)
                      : riveFileData?.link || ''
                  }
                  width={360}
                  height={125}
                  animationAutoPlay
                  controls={rive => {
                    riveInstance.current = rive;
                    return null;
                  }}
                />
                <ComponentActions>
                  <TooltipWrapper hoverText="Replace">
                    <DeleteButtonIcon
                      data-selector="btn-replace"
                      aria-label="Replace rive animation file"
                      onClick={() => {
                        clearRiveFile();
                        riveInstance.current = null;
                        setRiveFileData({
                          file: undefined,
                          fileName: undefined,
                          key: undefined,
                          link: undefined,
                        });
                        setIsShowRiveDropper(true);
                        setValue(RIVE_KEY, undefined);
                      }}
                    />
                  </TooltipWrapper>
                </ComponentActions>
              </RiveFileUploadWrapper>
            ) : (
              <FileDropperRive
                name={RIVE_KEY}
                inputDataSelector="rive-file-upload"
                accept={[ACCEPT_TYPE_RIVE]}
                validationError={errors[RIVE_KEY]?.message}
                additionalLabel={elementDimensionLabel}
                onChange={handleRiveFileChange}
              />
            )}
            {riveFileData?.key && (
              <Box $marginTop={4}>
                <SettingSmallTextEllipsis>
                  {riveFileData?.fileName}
                </SettingSmallTextEllipsis>
              </Box>
            )}
          </SettingsGroup>
          <SettingsGroup>
            <Box $justifyContent="space-between">
              <SettingsLabel>Static Image for Print</SettingsLabel>
              {imageData?.key && (
                <SettingSmallText marginBottom={0}>
                  {elementDimensionLabel}
                </SettingSmallText>
              )}
            </Box>
            <ImageUploadField
              fieldName={IMAGE_KEY}
              image={imageData ? imageData : null}
              elementDimensionLabel={elementDimensionLabel}
              error={errors[IMAGE_KEY]?.message}
              onImageUpload={handleFileChange}
              onDelete={handleDeleteImage}
            />
            {imageData?.key && (
              <Box $marginTop={4}>
                <SettingSmallTextEllipsis>
                  {imageData?.fileName}
                </SettingSmallTextEllipsis>
              </Box>
            )}
          </SettingsGroup>
          <ComponentImageSource sourceUrl={sourceUrl} onChange={setSourceUrl} />
          <SettingsGroup marginBottom={0}>
            <Box $justifyContent="space-between">
              <SettingsLabel htmlFor={ALT_TEXT_FIELD}>
                Alt Text
                <ComponentAltTextTooltip />
              </SettingsLabel>
              <PStyled marginBottom={0}>Optional</PStyled>
            </Box>
            <TextInputLimit
              rows={3}
              maxLength={ALT_TEXT_MAX}
              id={ALT_TEXT_FIELD}
              name={ALT_TEXT_FIELD}
              placeholder={
                loadingAltText
                  ? 'Generating Alt Text...'
                  : 'Describe this image'
              }
              defaultValue={imageData?.alt || ''}
              isDisabled={loadingAltText || isUpdating}
            />
          </SettingsGroup>
          <SimpleModalVerticalActionsStyled>
            <SecondaryLoadingButton
              data-selector="generate-alt-text"
              isLoading={loadingAltText}
              isDisabled={loadingAltText || isUpdating || !imageUrl}
              aria-disabled={loadingAltText || isUpdating || !imageUrl}
              onClick={() =>
                generateAltText({
                  variables: {
                    input: {
                      imageUrl: imageUrl || '',
                    },
                  },
                })
              }
            >
              {loadingAltText ? 'Generating Alt Text' : 'Generate Alt Text ✨'}
            </SecondaryLoadingButton>
            <SettingsGroup $marginTop={spacing16} marginBottom={0}>
              <CheckboxInput
                label="Auto-extract Rive Text Labels"
                checked={autoExtractLabels}
                disabled={isUpdating}
                onChange={e => setAutoExtractLabels(e.target.checked)}
              />
            </SettingsGroup>
            <PrimaryButton
              type="submit"
              $isDisabled={isUpdating}
              loading={isUpdating}
            >
              Save
            </PrimaryButton>
          </SimpleModalVerticalActionsStyled>
        </form>
      </FormProvider>
    </ActivityBuilderModal>
  );
};

const RiveModalWrapper = (props: Props) => (
  <FileDropperContextProvider>
    <RiveModal {...props} />
  </FileDropperContextProvider>
);

export default RiveModalWrapper;
